import { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { deletePrice } from '../../actions/certificationActions'
import { formatAmount, showGenericError } from '../../utility/certificationBuilder'
import PriceFormModal from './PriceFormModal'
import DeleteConfirmationModal from './DeleteConfirmationModal'

import classes from './PricesList.module.scss'

const PricesList = ({ certification }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [formPrice, setFormPrice] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [deletingPrice, setDeletingPrice] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const openForm = (price = null) => { setFormPrice(price); setShowForm(true) }
  const priceLabel = (price) => price.organization ? price.organization.name : t('certificationBuilder.prices.default')

  const handleDelete = () => {
    setIsDeleting(true)
    dispatch(deletePrice({ certificationId: certification.id, id: deletingPrice.id })).unwrap()
      .then(() => setDeletingPrice(null))
      .catch((errors) => (errors?.base ? toast.error(errors.base[0], { position: toast.POSITION.TOP_RIGHT }) : showGenericError()))
      .finally(() => setIsDeleting(false))
  }

  return (
    <div>
      <div className='d-flex align-items-baseline justify-content-between mb-2'>
        <span className={classes.label}>{t('certificationBuilder.prices.title')}</span>
        <button type='button' className={classes.linkButton} onClick={() => openForm()}>
          <i className='fa fa-plus mr-1' aria-hidden='true' />
          {t('certificationBuilder.prices.new')}
        </button>
      </div>

      {certification.prices.length === 0 && <p className={classes.empty}>{t('certificationBuilder.prices.empty')}</p>}

      <div className='d-flex flex-column'>
        {certification.prices.map(price => (
          <div key={price.id} className={classNames('d-flex align-items-center', classes.row)}>
            <span
              className={classNames(
                'd-inline-flex align-items-center justify-content-center', classes.icon, { [classes.iconDefault]: !price.organization }
              )}
            >
              <i className={price.organization ? 'fa fa-users' : 'fa fa-globe'} aria-hidden='true' />
            </span>
            <span className={classNames('flex-grow-1', classes.name)}>
              {priceLabel(price)}
              {!price.organization && <span className={classes.note}> · {t('certificationBuilder.prices.defaultNote')}</span>}
            </span>
            <span className={classes.amount}>{formatAmount(price)}</span>
            <button type='button' className={classes.editButton} onClick={() => openForm(price)}>{t('common.edit')}</button>
            <button type='button' className={classes.iconButton} title={t('common.delete')} onClick={() => setDeletingPrice(price)}>
              <i className='fa fa-trash-o' aria-hidden='true' />
            </button>
          </div>
        ))}
      </div>

      {showForm && (
        <PriceFormModal isOpen={showForm} certification={certification} price={formPrice} onClose={() => setShowForm(false)} />
      )}

      <DeleteConfirmationModal
        show={!!deletingPrice}
        title={t('certificationBuilder.prices.deleteTitle', { name: deletingPrice && priceLabel(deletingPrice) })}
        message={t(deletingPrice?.organization ? 'certificationBuilder.prices.deleteMessage' : 'certificationBuilder.prices.deleteDefaultMessage')}
        isLoading={isDeleting}
        onClose={() => setDeletingPrice(null)}
        onProceed={handleDelete}
      />
    </div>
  )
}

PricesList.propTypes = {
  certification: PropTypes.shape({
    id: PropTypes.number.isRequired,
    prices: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      amount: PropTypes.string.isRequired,
      currency: PropTypes.shape({ code: PropTypes.string.isRequired, symbol: PropTypes.string.isRequired }).isRequired,
      organization: PropTypes.shape({ name: PropTypes.string.isRequired }),
    })).isRequired,
  }).isRequired,
}

export default PricesList
